import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { Checkbox } from '@c2fo/liquidity';
import RadioInput from '@/enterprise/components/RadioInput';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { FrequencyType, OfferModalForm } from '../types';
import { SchedulingInput, Schedule } from './SchedulingInput';

export interface FrequencyInputProps {
  takerMarkets: TakerMarket[];
  schedule: Schedule;
  setStartDate: (date: string | undefined) => void;
  setEndDate: (date: string | undefined) => void;
  setCadence: (cadence: Schedule['cadence']) => void;
  errors: {
    cadence?: {
      message: string;
    };
  };
}

const FrequencyInput = ({
  takerMarkets,
  schedule,
  setStartDate,
  setEndDate,
  setCadence,
  errors,
}: FrequencyInputProps) => {
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext<OfferModalForm>();
  const frequency = watch('frequency');
  const enableSchedule = watch('enableSchedule');

  const handleFrequencyChange = (newFrequency: FrequencyType) => {
    setValue('frequency', newFrequency);

    // When switching to one-time, disable the schedule
    if (newFrequency === 'one-time') {
      setValue('enableSchedule', false);
    }
  };

  const handleEnableScheduleChange = (checked: boolean) => {
    setValue('enableSchedule', checked);

    // When disabling schedule, clear the schedule fields
    if (!checked) {
      setStartDate(undefined);
      setEndDate(undefined);
    }
  };

  return (
    <div className="space-y-3 p-4">
      <p className="text-sm">{t('scheduling.frequencyDescription')}</p>
      <div className="space-y-2">
        <div>
          <RadioInput
            checked={frequency === 'one-time'}
            description={t('scheduling.oneTimeDescription')}
            label={t('builder.oneTime')}
            onChange={() => handleFrequencyChange('one-time')}
            value="one-time"
            variant="light"
          />
        </div>
        <div>
          <RadioInput
            checked={frequency === 'recurring'}
            className={frequency === 'recurring' ? 'rounded-b-none border-b-0' : ''}
            description={t('scheduling.recurringDescription')}
            label={t('builder.recurring')}
            onChange={() => handleFrequencyChange('recurring')}
            value="recurring"
            variant="light"
          />
          {frequency === 'recurring' && (
            <div className="rounded-b border-[0.5px] border-t-0 border-primary-500 bg-white pt-0">
              <div className="pb-4 pl-11">
                <Checkbox
                  id="enable-schedule"
                  checked={enableSchedule}
                  onChange={(e) => handleEnableScheduleChange(e.target.checked)}
                  label={t('scheduling.enableSchedule')}
                />
              </div>
              {enableSchedule && (
                <>
                  <div className="px-4">
                    <div className="h-[1px] w-full bg-stroke" />
                  </div>
                  <div className="space-y-4 pl-7">
                    <SchedulingInput
                      takerMarkets={takerMarkets}
                      schedule={schedule}
                      setStartDate={setStartDate}
                      setEndDate={setEndDate}
                      setCadence={setCadence}
                      errors={errors}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FrequencyInput;
